import { motion } from 'framer-motion';
import {
  TrendingUp,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  Target,
  Users,
  Zap,
  BarChart3,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function ContinuousImprovementPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const pdcaPhases = [
    {
      phase: 'Plan',
      icon: Target,
      subtitle: 'Identify Improvement Opportunity',
      description:
        'Define the problem, analyze root causes, and develop hypothesis for improvement. Data-driven problem definition, not gut feel.',
      activities: [
        'Define current state vs. desired state (the gap)',
        'Analyze root causes (5 Whys, Fishbone diagram)',
        'Review relevant data and metrics',
        'Generate improvement hypotheses',
        'Design experiment or pilot',
        'Set success criteria',
      ],
      questions: [
        'What specifically is the problem? (Be precise)',
        "What's the root cause, not symptom?",
        'What data do we have?',
        'What do we hypothesize will improve it?',
        'How will we know if it works?',
      ],
      tip: 'Spend 70% of time in Plan. Rushed planning = poor execution. Most improvement failures happen here—problem not understood, root cause not found.',
    },
    {
      phase: 'Do',
      icon: Zap,
      subtitle: 'Implement the Change',
      description:
        "Execute the planned improvement on small scale. Pilot, don't deploy enterprise-wide yet. Learn fast and cheap.",
      activities: [
        'Implement change in controlled environment (pilot)',
        "Document what you're doing as you do it",
        'Collect data throughout implementation',
        'Observe actual vs. expected outcomes',
        'Note unexpected effects (positive or negative)',
        'Keep scope small and time-boxed',
      ],
      questions: [
        'Are we implementing exactly what we planned?',
        "What's different from our hypothesis?",
        'What data are we collecting?',
        "Who's observing and documenting?",
        "What's our pilot duration and success criteria?",
      ],
      tip: 'Start small. Pilot with 1 team, 1 customer segment, 1 product line. Prove it works before scaling. Failed pilots are cheap; failed full rollouts are disasters.',
    },
    {
      phase: 'Check',
      icon: BarChart3,
      subtitle: 'Measure Results',
      description:
        "Analyze data from the pilot. Did the improvement hypothesis prove true? What worked? What didn't? Honest assessment, no spin.",
      activities: [
        'Compare actual results vs. success criteria',
        'Analyze quantitative data (metrics moved?)',
        'Gather qualitative feedback (user experience)',
        "Identify what worked and what didn't",
        'Document lessons learned',
        'Calculate ROI if applicable',
      ],
      questions: [
        'Did we hit our success criteria? (Yes/No/Partial)',
        'What does the data actually say?',
        'What surprised us?',
        'What would we do differently?',
        'Should we scale, modify, or abandon?',
      ],
      tip: "Be brutally honest. If improvement didn't work, admit it fast. Sunk cost fallacy kills organizations. No improvement = kill it, learn from it, try something else.",
    },
    {
      phase: 'Act',
      icon: CheckCircle,
      subtitle: 'Standardize or Adjust',
      description:
        "Based on Check results: Scale the improvement (worked), modify and retry (partially worked), or abandon (didn't work). Document new standard.",
      activities: [
        'Decision: Scale / Modify / Abandon',
        'If scaling: Document new standard process',
        'If scaling: Train broader team',
        'If scaling: Update systems/tools',
        'If modifying: Plan next PDCA cycle',
        'If abandoning: Document why and what learned',
      ],
      questions: [
        "What's our decision based on data?",
        "If scaling, what's the rollout plan?",
        'How do we ensure it sticks (not reverts)?',
        "What's the new standard operating procedure?",
        'What did we learn for next improvement?',
      ],
      tip: "Act = decide and execute. Don't let pilots linger in limbo. Make call: scale it, fix it, or kill it. Indecision is waste. Then start next PDCA cycle—continuous means continuous.",
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Execution & Monitoring', path: '/execution-monitoring' },
              { label: 'Continuous Improvement' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <TrendingUp className="w-4 h-4 mr-2" />
            Execution & Monitoring Tool 3 of 4
          </span>
        }
        title="Learn from the work and improve the plan"
        subtitle="Use small experiments and clear feedback to make your strategy more useful over time. The PDCA cycle helps your team turn observations into a considered next step."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Plan your first improvement
        </Link>
      </PageHero>

      {/* PDCA Cycle Deep Dive */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Make learning part of the way you execute</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Plan, Do, Check, Act gives an improvement idea a clear path. Define the problem, try a
              change, review the evidence, and decide what to carry forward.
            </p>
          </motion.div>
          <div className="max-w-6xl mx-auto space-y-8">
            {pdcaPhases.map((item, idx) => (
              <motion.div
                key={idx}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center font-bold text-2xl">
                    {idx + 1}
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <item.icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{item.phase}</h3>
                      <p className="text-sm text-blue-700 font-medium">{item.subtitle}</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 mb-6 text-lg">{item.description}</p>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900 mb-3">Key Activities:</p>
                    <ul className="space-y-2">
                      {item.activities.map((activity, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <CheckCircle className="w-3 h-3 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                          {activity}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900 mb-3">Critical Questions:</p>
                    <ul className="space-y-2">
                      {item.questions.map((q, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <Target className="w-3 h-3 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Building Improvement Culture */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                How to Build a Continuous Improvement Culture
              </h2>
              <p className="text-xl text-gray-600">
                Six foundational practices for organizational learning
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Make Failure Safe (Psychological Safety)',
                  description:
                    'Improvement requires experimentation. Experiments sometimes fail. If failure = punishment, no one experiments. Culture must celebrate learning, even from failure.',
                  example:
                    'Amazon: "If you\'re not failing, you\'re not innovating enough." Google: "Celebrate intelligent failures." Make it safe to try, fail, and learn.',
                  tip: 'Run "failure retrospectives" where teams share what didn\'t work and what they learned. Make it prestigious to share failures. Leaders go first—share their failures openly.',
                },
                {
                  step: '2',
                  title: 'Time-Box Improvement Work (20% Time)',
                  description:
                    'Continuous improvement isn\'t "in spare time." It requires dedicated capacity. Best practice: 20% of team capacity for improvement experiments.',
                  example:
                    "Google's 20% time created Gmail, AdSense. 3M's 15% time created Post-it Notes. Allocate explicit capacity or improvement never happens.",
                  tip: 'Start with 10% if 20% feels impossible. One day every two weeks for improvement experiments. Track it. Protect it. Measure ROI to justify continuing.',
                },
                {
                  step: '3',
                  title: 'Measure Learning Velocity',
                  description:
                    'Most companies measure revenue, profit, customers. Few measure speed of learning. How many PDCA cycles per quarter? How fast from idea to pilot to decision?',
                  example:
                    'Track: # experiments run, cycle time (idea → decision), success rate, ROI of improvements. Make learning velocity a KPI.',
                  tip: 'Goal: Double your PDCA cycles per quarter. Going from 4 experiments/quarter to 8 doubles learning rate. Speed compounds into competitive advantage.',
                },
                {
                  step: '4',
                  title: 'Democratize Problem-Solving (Bottom-Up)',
                  description:
                    'Best improvement ideas come from people closest to work, not executives. Empower frontline to identify problems and run experiments.',
                  example:
                    'Toyota: Any assembly line worker can stop production if they see problem (Andon cord). Empowerment = improvement. Micromanagement = stagnation.',
                  tip: 'Create "improvement boards" visible to all: Problem → Hypothesis → Experiment → Result. Anyone can add. Transparency drives engagement and learning.',
                },
                {
                  step: '5',
                  title: 'Share Learning Across Teams',
                  description:
                    'Improvement in Team A stays in Team A. Team B solves same problem independently. Waste. Sharing multiplies impact of each experiment.',
                  example:
                    'Create "learning library": Every PDCA cycle documented. Searchable. Tagged. Team B finding Team A\'s solution = 10x ROI on original experiment.',
                  tip: 'Monthly "learning showcase": Teams present experiments (success or failure) to broader organization. Cross-pollinate ideas. Steal shamelessly.',
                },
                {
                  step: '6',
                  title: 'Link Improvement to Strategy',
                  description:
                    'Random improvements = busy work. Strategic improvements = competitive advantage. Every PDCA cycle should connect to strategic objective or key metric.',
                  example:
                    'Strategic Objective: "Reduce customer churn 18% → 12%" → Improvement sprints on onboarding, support response time, product UX. All ladder up.',
                  tip: 'Filter improvements: "Does this advance a strategic objective or improve a key metric?" If no to both, it\'s nice-to-have, not strategic. Defer.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-green-50 p-3 rounded-lg mb-3 border-l-4 border-green-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-green-700">Example:</span>{' '}
                          {item.example}
                        </p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Continuous Improvement Mistakes</h2>
              <p className="text-xl text-gray-600">Pitfalls that kill improvement momentum</p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Skipping "Plan" Phase',
                  description:
                    "Jumping straight to solution without understanding problem. Result: solving wrong problem, or solving symptom not root cause. Improvement doesn't stick.",
                  fix: "Force discipline: Can't move to \"Do\" until root cause identified with data. Use 5 Whys. If you can't explain root cause clearly, you don't understand problem yet.",
                },
                {
                  mistake: 'Scaling Before Validating',
                  description:
                    "Running pilot, seeing mixed results, rolling out enterprise-wide anyway. Then discovering improvement doesn't work at scale. Expensive failure.",
                  fix: 'Strict rule: Must hit success criteria in pilot before scaling. No exceptions. If pilot partially worked, iterate (another PDCA cycle) before scaling.',
                },
                {
                  mistake: 'No Time Allocated',
                  description:
                    'Expecting improvement "in spare time" while everyone is 100%+ utilized. Result: no experiments run. Status quo persists. No learning.',
                  fix: 'Explicit capacity allocation: 10-20% time for improvement. Put it in performance goals. Track it. Protect it from "urgent" work creep.',
                },
                {
                  mistake: 'Punishing Failed Experiments',
                  description:
                    'Team runs experiment, it fails, they get blamed. Next time, no one volunteers. Improvement dies. Fear kills innovation.',
                  fix: 'Celebrate intelligent failures publicly. Reward teams that run experiments, even if they fail, if they learn. Punish not trying, not failing.',
                },
                {
                  mistake: 'Not Documenting Learnings',
                  description:
                    "Running experiments, learning lessons, but knowledge stays in people's heads. New team members repeat same mistakes. Learning doesn't compound.",
                  fix: 'Every PDCA cycle = written summary. Problem, hypothesis, experiment, result, decision, learning. Stored in searchable system. Make it easy to find past learnings.',
                },
                {
                  mistake: 'Improvement Theater',
                  description:
                    "Running improvement sprints because it's trendy, but no real commitment. Suggestions ignored. Nothing changes. Team becomes cynical.",
                  fix: 'Leaders must act on improvements. If 10 experiments run, at least 3-5 should result in actual changes. If nothing ever changes, stop pretending—fix leadership first.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">Turn feedback into the next useful change</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Frame the experiment</h3>
                <p className="text-gray-600">
                  State the problem, the change you want to test, and the evidence you will review.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Check what happened</h3>
                <p className="text-gray-600">
                  Compare the result with your expectations and capture what your team learned.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Adapt the approach</h3>
                <p className="text-gray-600">
                  Keep, adjust, or stop the change, then bring that learning into the next planning
                  cycle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/execution-monitoring/okrs-kpis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Target className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">OKRs & KPIs</h3>
                <p className="text-gray-600 text-sm mb-3">Link improvements to strategic metrics</p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring/strategic-cadence"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <TrendingUp className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Strategic Cadence</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Review improvements at each cadence level
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring/accountability-ownership"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Users className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">
                  Accountability & Ownership
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Assign owners for improvement experiments
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/execution-monitoring/strategic-cadence"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Strategic Cadence
              </Link>
              <Link
                to="/execution-monitoring/accountability-ownership"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Accountability & Ownership <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Plan your first improvement in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Keep, adjust, or stop the change, then bring that learning into the next planning cycle.
            Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Plan your first improvement <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
